import React, { useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface ProductGalleryProps {
  images: string[];
  sku: string;
}

function ImageWithSkeleton({ src, alt, className, loading = 'eager' as 'eager' | 'lazy', onClick }: {
  src: string; alt: string; className: string; loading?: 'eager' | 'lazy'; onClick?: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className={`relative flex items-center justify-center bg-[#F8FAFC] ${className}`} onClick={onClick}>
      {!loaded && !error && (
        <div className="absolute inset-0 bg-secondary/40 animate-pulse rounded-xl" />
      )}
      {error ? (
        <div className="absolute inset-0 bg-secondary/20 flex items-center justify-center rounded-xl">
          <span className="text-xs text-muted-foreground">Immagine non disponibile</span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className={`max-w-full max-h-full object-contain transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loading={loading}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      )}
    </div>
  );
}

export function ProductGallery({ images, sku }: ProductGalleryProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const activeImage = images[activeIdx] || images[0];
  const bigImage = activeImage.replace('w=600', 'w=1200');

  const goNext = useCallback(() => {
    setActiveIdx(i => (i + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setActiveIdx(i => (i - 1 + images.length) % images.length);
  }, [images.length]);

  return (
    <>
      <div data-testid="product-gallery">
        {/* Immagine principale — nessuno zoom al passaggio del mouse
            (rimosso su richiesta utente); il click apre il lightbox. */}
        <div
          className="relative rounded-xl border bg-white overflow-hidden cursor-pointer group"
          onClick={() => setLightbox(true)}
          data-testid="gallery-main-image"
        >
          <ImageWithSkeleton
            src={activeImage}
            alt={`${sku} - immagine ${activeIdx + 1}`}
            className="w-full h-[300px] sm:h-[360px]"
          />

          {/* Nav arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 backdrop-blur flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 backdrop-blur flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}

          {/* Counter */}
          <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-black/50 text-white text-[10px] font-medium">
            {activeIdx + 1} / {images.length}
          </div>
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="flex gap-2 mt-2">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`w-16 h-16 rounded-lg border-2 overflow-hidden flex-shrink-0 transition-all duration-150 ${
                  i === activeIdx
                    ? 'border-[hsl(var(--dtk-focus))] ring-1 ring-[hsl(var(--dtk-focus))]/30'
                    : 'border-transparent hover:border-border opacity-60 hover:opacity-100'
                }`}
                data-testid={`gallery-thumb-${i}`}
              >
                <ImageWithSkeleton
                  src={img.replace('w=600', 'w=100')}
                  alt={`${sku} thumb ${i + 1}`}
                  className="w-full h-full"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={() => setLightbox(false)}
          data-testid="lightbox-overlay"
          role="dialog"
          aria-label="Immagine ingrandita"
        >
          <button
            onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <img
            src={bigImage}
            alt={`${sku} fullscreen`}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setActiveIdx(i); }}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${i === activeIdx ? 'bg-white' : 'bg-white/30 hover:bg-white/50'}`}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
}
